import React, { useState, useEffect, useContext } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import { StateContext } from "../../store/context";
import { TitleArea } from "../../components/Typography/titleArea";
import { LoadingData, NoDataDetails } from "./components/noDetails";
import AdBanner from "../../components/AdBanner";
import Entitlement from "../../components/Entitlements";
import { StockSelectOptions } from "./components/stockSelectOptions";
import { FinancialReports } from "./components/FinancialReports";
import { usePreventCopy } from "../../hooks/usePreventCopy";
import DisclaimerButton from "../../components/DisclaimerButton";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./financials.theme.css";

const FinancialsScreen: React.FC = (): React.ReactElement => {
  usePreventCopy();
  const {
    baseCurrency,
    authUser: { allowed, sub_plan, role_name },
  } = useContext(StateContext);

  const [adverts, setAds] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("income_statement");
  const [selectedStock, setSelectedStock] = useState<any>(null);

  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  const {
    response: ads,
    loading: adsLoading,
    execute: getAds,
  } = useFetchApi("/api/ads", {}, "GET");

  const {
    execute: getProfile,
    response: profileResponse,
    loading: profileLoading,
  } = useFetchApi("/api/company/profiles", {
    stock_ids: selectedStock,
  });

  useEffect(() => {
    getStocks();
    getAds();
  }, []);

  useEffect(() => {
    if (ads && !adsLoading) {
      setAds(ads);
    }
  }, [ads, adsLoading]);

  // Effect to handle when a stock is selected
  useEffect(() => {
    if (selectedStock) {
      getProfile({ stock_ids: [selectedStock.id] });
      // setActiveTab("income_statement");
    }
  }, [selectedStock]);

  useEffect(() => {
    if (profileResponse && !profileLoading) {
      setProfileData(
        profileResponse?.data?.[0] || profileResponse?.[0] || profileResponse,
      );
    }
  }, [profileResponse, profileLoading]);

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
  };

  const getMethodologyContent = () => {
    if (!profileData) {
      return "";
    }

    const methodologyMap: { [key: string]: string } = {
      income_statement: profileData.income_statement_methodology,
      balance_sheet: profileData.balance_sheet_methodology,
      cash_flow: profileData.cash_flow_methodology,
    };

    const content = methodologyMap[activeTab] || "";

    if (content == null || content === "null" || content.trim() === "") {
      return "";
    }

    return content;
  };

  const isPremiumUser =
    sub_plan === "Premium Plan" && role_name === "Premium Customer"
      ? true
      : false;
  const isBasicCustomer =
    sub_plan === "Free Plan" && role_name === "Basic Customer";

  const methodologyContent = getMethodologyContent();

  return (
    <div className="vi-fin flex md:flex-row flex-col items-center md:items-start w-full select-none">
      <div
        className={`flex flex-col w-full md:w-full h-full  items-start px-4 py-3 pb-10`}
      >
        <div className="w-full flex items-start justify-between gap-4">
          <TitleArea
            variant="page"
            title="Financials"
            subTitle="Your complete financial snapshot — simplified, structured, and insightful."
          />
          <DisclaimerButton />
        </div>
        <hr className="hr-line w-full my-2" />
        <div className="w-full md:w-[500px]">
          <StockSelectOptions
            selectedStock={selectedStock}
            setSelectedStock={setSelectedStock}
          />
        </div>

        <div className={`w-full`}>
          <hr className="hr-line w-full my-2" />
          {loading ? (
            <LoadingData />
          ) : !loading ? (
            <FinancialReports
              selectedStock={selectedStock}
              profileData={profileData}
              onTabChange={handleTabChange}
            />
          ) : (
            <div className="chat-container"></div>
          )}
        </div>

        {selectedStock && methodologyContent && (
          <div className="vi-glass-card vi-fin-card w-full p-6 mt-6">
            <h3 className="text-[19px] font-semibold font-dm m-0 mb-3 text-[color:var(--vi-text)]">
              Methodology
            </h3>
            <p className="text-sm leading-relaxed mb-4 text-[color:var(--vi-text-secondary)]">
              Financials are presented same as the published financial
              statement line items, except for the following items, which
              have been derived using the calculation methodologies outlined
              below:
            </p>
            <div className="space-y-3">
              {methodologyContent
                .split(/\d+\)\s*/)
                .filter(Boolean)
                .map((item, index) => {
                  const [label, ...rest] = item.split(":");
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <span
                        className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold flex-shrink-0 mt-0.5"
                        style={{
                          background: "var(--vi-accent-grad)",
                          color: "var(--vi-accent-on)",
                        }}
                      >
                        {index + 1}
                      </span>
                      <p className="text-sm leading-relaxed m-0">
                        <span className="font-semibold text-[color:var(--vi-text)]">
                          {label.trim()}:
                        </span>{" "}
                        <span className="text-[color:var(--vi-text-secondary)]">
                          {rest.join(":").trim()}
                        </span>
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* <div
        className={`flex flex-col lg:w-3/12  ${
          false ? " hidden" : "md:!w-3/12"
        } !max-h-screen h-screen gap-8 mx-2 items-center relative px-2 py-4`}
      >
        <div className="w-[1px] h-full bg-[#D0D5DD] absolute top-[2px]  -left-2" />
        {adverts && adverts.length ? (
          <>
            {adverts
              .filter((ad: any) => ad.section === "financials")
              .map((ad: any) => (
                <AdBanner key={ad.id} ad={ad} />
              ))}
            <Entitlement allowedKeys={["ADD_ADS"]}>
              <AdBanner />
            </Entitlement>
          </>
        ) : null}
      </div> */}
    </div>
  );
};

export default FinancialsScreen;
